import { alpha, Box, Typography } from "@mui/material";
import { EffortStats } from "../../types";
import { roundMinutesToNearestHalf } from "~/lib/date";
import { useColorMode } from "~/providers/color-mode";
import { motion } from "motion/react";
import {
  ANIMATION_DELAYS,
  ANIMATION_DURATIONS,
} from "~/pages/integrations/security-pipeline/animation";
import AnimatedNumber from "~/components/AnimatedNumber";

interface RemediationEffortCardProps {
  manualEffort: EffortStats;
  animationKey: number;
}

export const RemediationEffortCard = ({
  manualEffort,
  animationKey,
}: RemediationEffortCardProps) => {
  const { mode } = useColorMode();
  const isLight = mode === "light";
  const { research, scripting, verification, rollout, total } = manualEffort;

  const categories = [
    {
      key: "Research & Planning",
      value: research,
    },
    {
      key: "Coding & Testing",
      value: scripting,
    },
    {
      key: "Verification",
      value: verification,
    },
    {
      key: "Staged Rollout",
      value: rollout,
    },
  ];

  const totalManualEffort = roundMinutesToNearestHalf(total);

  return (
    <Box
      key={animationKey}
      component={motion.div}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: ANIMATION_DURATIONS.box,
        delay: ANIMATION_DELAYS.grayBox,
        type: "spring",
        stiffness: 120,
        damping: 20,
        bounce: 0.1,
      }}
      sx={{
        position: "relative",
        p: 3,
        backgroundColor: isLight
          ? alpha("#000000", 0.06)
          : alpha("#FFFFFF", 0.12),
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "stretch",
          mt: 0.25,
          mb: 1.5,
        }}
      >
        <Typography
          color={isLight ? "text.primary" : "text.secondary"}
          fontSize={60}
          lineHeight="64px"
          fontWeight={900}
          textAlign="center"
          letterSpacing="-1.5px"
        >
          <AnimatedNumber
            value={totalManualEffort}
            delay={ANIMATION_DELAYS.grayCounter}
            duration={ANIMATION_DURATIONS.grayCounterTotal}
            isDecimal
            animationKey={1}
          />
        </Typography>
        <Typography
          textAlign="center"
          color={isLight ? "text.primary" : "text.secondary"}
          fontSize={10}
          fontWeight={600}
        >
          hours manually
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
          px: 0,
          py: 0.5,
          alignSelf: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.25,
            alignSelf: "stretch",
          }}
        >
          <Typography
            sx={{
              display: "flex",
              height: 10,
              flexDirection: "column",
              justifyContent: "center",
              flex: "1 0 0",
              color: isLight ? "text.primary" : "text.secondary",
              fontSize: 10,
              lineHeight: "10px",
              letterSpacing: ".4px",
              textTransform: "uppercase",
              opacity: 0.5,
            }}
          >
            task
          </Typography>
          <Typography
            sx={{
              color: isLight ? "text.primary" : "text.secondary",
              fontSize: 10,
              lineHeight: "10px",
              letterSpacing: ".4px",
              textTransform: "uppercase",
              opacity: 0.5,
            }}
          >
            hours
          </Typography>
        </Box>
        {categories.map((c, i) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.25,
              alignSelf: "stretch",
            }}
          >
            <Typography
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: "1 0 0",
                color: isLight ? "text.primary" : "text.secondary",
                fontSize: 10,
                fontWeight: 500,
                lineHeight: "12px",
                letterSpacing: ".4px",
              }}
            >
              {c.key}
            </Typography>
            <Typography
              sx={{
                color: isLight ? "text.primary" : "text.secondary",
                textAlign: "right",
                fontSize: 10,
                lineHeight: "12px",
                letterSpacing: ".4px",
              }}
            >
              <AnimatedNumber
                value={roundMinutesToNearestHalf(c.value)}
                delay={1 + Number(`0.${i}`)}
                duration={ANIMATION_DURATIONS.counter}
                animationKey={animationKey}
                isDecimal
              />
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
